import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService, CampaignApiService } from '@digilize/shared/data/services';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { APP_CONFIG } from '@digilize/shared/utils/tokens';
import { AppConfig, PossibleApiPrefix, Token } from '@shared/definitions';
import * as moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  DialogLostSessionComponent,
  DialogLostSessionComponentData,
} from './dialog-lost-session/dialog-lost-session.component';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_EXPIRATION = 1; // In minutes
  private readonly REFRESH_TOKEN_EXPIRATION = 60 * 24 * 365 * 100; // In minutes (100years)
  private USER_INTERACTED_WITH_APP = false;

  constructor(
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private authApiService: AuthApiService,
    private cookieService: CookieService,
    private dialogService: DialogService,
    private router: Router,
    private campaignApiService: CampaignApiService,
    private snackbarService: SnackbarService
  ) {
    window.addEventListener('click', () => {
      this.USER_INTERACTED_WITH_APP = true;
    });
  }

  isLoggedIn(): boolean {
    return !!this.getRefreshToken();
  }

  getAccessToken(apiPrefix: PossibleApiPrefix = 'api'): string | null {
    const token = localStorage.getItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix));
    const tokenExpiration = localStorage.getItem(this.patchWithAppLocalAuthPrefix('tokenExpiration', apiPrefix));
    if (Number(tokenExpiration) > moment.now()) {
      return token;
    }
    return null;
  }

  getRefreshToken(apiPrefix: PossibleApiPrefix = 'api'): string | null {
    const token = localStorage.getItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix));
    const tokenExpiration = localStorage.getItem(this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix));

    if (Number(tokenExpiration) > moment.now()) {
      return token;
    }

    return null;
  }

  logout(logoutRoute: string | null = '/'): void {
    this.removeSession();

    if (logoutRoute) {
      window.location.href = logoutRoute;
    }
  }

  softLogout(logoutRoute: string | null = '/'): void {
    this.removeSession();

    if (logoutRoute) {
      this.router.navigate([logoutRoute]);
    }
  }

  refreshAccessToken(apiPrefix: PossibleApiPrefix = 'api'): Observable<null | Token> {
    const { token, refreshExpiration } = this.getToken(apiPrefix);

    if (refreshExpiration && Number(refreshExpiration) > moment.now()) {
      return this.authApiService.refreshToken(token, apiPrefix).pipe(
        tap({
          next: (newToken: Token) => {
            this.setSession(newToken, null, apiPrefix);
          },
          error: (error) => {
            // Logout if it can't get refresh token from API
            const isAdminPanel = window.location.href.includes('/admin');
            if (error.status === 429) {
              this.snackbarService.openSnackbarWithApiError(error.error.error, 20000);
              return of(null);
            }
            if (isAdminPanel && this.USER_INTERACTED_WITH_APP) {
              const dialog = this.dialogService.openDialog(DialogLostSessionComponent);
              dialog.afterClosed().subscribe(() => this.softLogout('/auth'));
            } else {
              this.logout();
              window.location.reload();
            }
          },
        })
      );
    }

    return of(null);
  }

  refreshAccessTokenManualRetry(apiPrefix: PossibleApiPrefix = 'api'): Observable<Token | null> {
    const { token, refreshExpiration } = this.getToken(apiPrefix);

    if (refreshExpiration && Number(refreshExpiration) > moment.now()) {
      return this.authApiService.refreshToken(token, apiPrefix).pipe(
        tap({
          next: (newToken: Token) => {
            this.setSession(newToken, null, apiPrefix);
          },
          error: (error) => {
            this.dialogService.openDialog<DialogLostSessionComponentData>(DialogLostSessionComponent, {
              data: { closeable: false },
              disableClose: true,
            });
            if (error.status === 429) {
              this.snackbarService.openSnackbarWithApiError(error.error.error, 20000);
              return of(null);
            }
          },
        })
      );
    }

    return of(null);
  }

  setSession(token: Token, callback = null, apiPrefix: PossibleApiPrefix = 'api'): void {
    const tokenExpiration = moment().add(this.TOKEN_EXPIRATION, 'minutes');
    const refreshTokenExpiration = moment().add(this.REFRESH_TOKEN_EXPIRATION, 'minutes');

    if (token.token) {
      localStorage.setItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix), token.token);
      localStorage.setItem(
        this.patchWithAppLocalAuthPrefix('tokenExpiration', apiPrefix),
        JSON.stringify(tokenExpiration.valueOf())
      );
    }

    if (token.refresh_token) {
      localStorage.setItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix), token.refresh_token);
      localStorage.setItem(
        this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix),
        JSON.stringify(refreshTokenExpiration.valueOf())
      );
    }
    this.authApiService.getMe();

    // Claim cart session
    if (callback) {
      const claimCallback = (): void => {
        if (localStorage.getItem('checkout_return')) {
          const returnUrl = localStorage.getItem('checkout_return');
          localStorage.removeItem('checkout_return');
          window.location.href = returnUrl;
        } else {
          callback();
        }
      };

      const sessionId = this.cookieService.get('session_id');
      if (sessionId) {
        this.authApiService.claimCartSession(sessionId).subscribe({
          next: () => claimCallback(),
          error: () => claimCallback(),
        });
      } else {
        claimCallback();
      }
    }
  }

  setAcquisitionSession(token: Token, redirectUrl: string, returnUrl: string, app: string): void {
    this.setSession(token, () => {
      const acquisitionId = this.cookieService.get('acquisition_id');
      if (acquisitionId) {
        this.registerAcquisition(acquisitionId, redirectUrl, returnUrl, app);
      } else {
        window.location.href =
          app === 'dashboard' || this.appConfig.app === 'flixit'
            ? '/admin/auth'
            : decodeURIComponent(redirectUrl) + returnUrl;
      }
    });
  }

  private registerAcquisition(id: string, redirectUrl?: string, returnUrl?: string, app?: string): void {
    this.campaignApiService.registerAcquisition(id).subscribe({
      next: () => {
        this.cookieService.delete('acquisition_id');
        window.location.href =
          app === 'dashboard' || this.appConfig.app === 'flixit'
            ? '/admin/c/0/shops'
            : decodeURIComponent(redirectUrl) + returnUrl;
      },
      error: (error) => {
        if (error?.status === 400) {
          this.cookieService.delete('acquisition_id');
        }

        if (error.error?.error?.info?.kind === 'already_exists') {
          window.location.href =
            app === 'dashboard' || this.appConfig.app === 'flixit'
              ? '/admin/c/0/shops'
              : decodeURIComponent(redirectUrl) + returnUrl;
        } else {
          this.router.navigate(['niet-gevonden']);
        }
      },
    });
  }

  private patchWithAppLocalAuthPrefix(localStorageKey: string, apiPrefix: PossibleApiPrefix = 'api'): string {
    /* to distinguish localstorage keys in certain apps (when deployed on same domain apps can share localstorage)  */
    switch (apiPrefix) {
      case 'api':
        const prefix = this.appConfig.localStorageAuthPrefix || '';
        return `${prefix}${localStorageKey}`;
      case 'api2':
        const prefix2 = this.appConfig.localStorageAuthPrefix2 || '';
        return `${prefix2}${localStorageKey}`;
      default:
        throw new Error(`${apiPrefix} is not handled in patchWithAppLocalAuthPrefix`);
    }
  }

  private removeSession(): void {
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('token'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('tokenExpiration'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('refresh_token'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('refreshTokenExpiration'));
    this.cookieService.delete('session_id', '/');
  }

  private getToken(apiPrefix: PossibleApiPrefix = 'api'): { token: Token; refreshExpiration: string } {
    const token: Token = {
      token: localStorage.getItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix)),
      refresh_token: localStorage.getItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix)),
    };
    const refreshExpiration = localStorage.getItem(
      this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix)
    );

    return { token, refreshExpiration };
  }
}
